import * as Sentry from "@sentry/react";
import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";

import {
  CreateTutorialRequest,
  TutorialItem,
  UpdateTutorialRequest
} from "components/user-documentation/types";

const TUTORIALS_API_BASE = `${process.env.REACT_APP_USER_DOCUMENTATION_SERVICE}/tutorials`;

/**
 * Get all tutorials sorted by order (ascending)
 */
export async function getTutorials(search = ""): Promise<TutorialItem[]> {
  try {
    // With a search term the service returns matching videos best-first; without
    // one it returns them all in display order.
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    const response = await axios.get<TutorialItem[]>(`${TUTORIALS_API_BASE}${params}`);
    // Ensure we always return an array, even if backend returns null/undefined
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    // Log to Sentry but re-throw so React Query can handle it
    Sentry.captureException(error);
    throw error;
  }
}

/**
 * Create a new tutorial
 */
export async function createTutorial(data: CreateTutorialRequest): Promise<TutorialItem> {
  const response = await axios.post<TutorialItem>(TUTORIALS_API_BASE, data);
  return response.data;
}

/**
 * Update an existing tutorial
 */
export async function updateTutorial(
  id: string,
  data: UpdateTutorialRequest
): Promise<TutorialItem> {
  const response = await axios.put<TutorialItem>(`${TUTORIALS_API_BASE}/${id}`, data);
  return response.data;
}

/**
 * Delete a tutorial
 */
export async function deleteTutorial(id: string): Promise<void> {
  await axios.delete(`${TUTORIALS_API_BASE}/${id}`);
}

/**
 * Replaces the search keywords on a tutorial.
 *
 * Separate from updateTutorial because the update overwrites every field it is
 * given, so a payload without keywords would erase them.
 */
export async function setTutorialKeywords(
  id: string,
  keywords: string[]
): Promise<string[]> {
  const response = await axios.put<{ keywords: string[] }>(
    `${TUTORIALS_API_BASE}/${id}/keywords`,
    { keywords }
  );
  return response.data?.keywords ?? [];
}

/**
 * Hook to fetch all tutorials
 */
export function useGetTutorials(search = "", enabled = true) {
  // The search term is part of the key, so changing it fetches afresh rather
  // than showing the previous term's results.
  //
  // enabled keeps a search typed on another tab from fetching videos nobody is
  // looking at — each such request also costs the service a vocabulary read.
  return useQuery<TutorialItem[], Error>(
    ["tutorials", search],
    () => getTutorials(search),
    { enabled }
  );
}

/**
 * Hook to create a new tutorial
 */
export function useCreateTutorial() {
  // Deliberately does not refetch the list here. Keywords are written by a
  // separate call after this one resolves, and refetching first put a copy
  // without them into the cache — which the edit form then saved back as empty,
  // erasing curated keywords. The caller refreshes once everything is written.
  return useMutation<TutorialItem, Error, CreateTutorialRequest>(createTutorial);
}

/**
 * Hook to update an existing tutorial
 */
export function useUpdateTutorial() {
  // See useCreateTutorial: refreshing before the keywords call lands is what
  // wiped them.
  return useMutation<TutorialItem, Error, { id: string; data: UpdateTutorialRequest }>(
    ({ id, data }) => updateTutorial(id, data)
  );
}

/**
 * Hook to delete a tutorial
 */
export function useDeleteTutorial() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>(deleteTutorial, {
    onSuccess: () => {
      queryClient.invalidateQueries(["tutorials"]);
    }
  });
}
